import { useCallback, useEffect, useRef, useState } from 'react';

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
const RICH_MOTION_QUERY = '(min-width: 900px) and (pointer: fine)';

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(query).matches
  ));

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function usePrefersReducedMotion() {
  return useMediaQuery(REDUCED_QUERY);
}

let revealObserver;

function getRevealObserver() {
  if (!revealObserver) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-inview', '');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -4% 0px', threshold: 0.05 });
  }
  return revealObserver;
}

// Marks the element with [data-inview] once it enters the viewport; CSS drives the animation.
// Returns a callback ref so elements that mount late (after data loads) are still observed.
export function useReveal() {
  const elementRef = useRef(null);

  return useCallback((element) => {
    const previous = elementRef.current;
    if (previous && previous !== element && revealObserver) {
      revealObserver.unobserve(previous);
    }
    elementRef.current = element;
    if (!element || element.hasAttribute('data-inview')) {
      return;
    }
    if (!('IntersectionObserver' in window)) {
      element.setAttribute('data-inview', '');
      return;
    }
    getRevealObserver().observe(element);
  }, []);
}

export function useInView(options = { threshold: 0.3 }) {
  const [element, setElement] = useState(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!element || inView) {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, inView]);

  return [setElement, inView];
}

// Translates the element relative to its position in the viewport. Desktop + motion-allowed only.
export function useParallax(strength = 0.12) {
  const [element, setElement] = useState(null);
  const reduced = usePrefersReducedMotion();
  const rich = useMediaQuery(RICH_MOTION_QUERY);

  useEffect(() => {
    if (!element) {
      return undefined;
    }
    if (reduced || !rich) {
      element.style.transform = '';
      return undefined;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = element.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        return;
      }
      const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
      element.style.transform = `translate3d(0, ${(progress * strength * -100).toFixed(2)}%, 0)`;
    };
    const onScroll = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [element, strength, reduced, rich]);

  return setElement;
}

export function useCountUp(target, active, duration = 1400) {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    if (reduced) {
      setValue(target);
      return undefined;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(target * eased));
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [target, active, duration, reduced]);

  return value;
}

export function useScrollState({ scrolledAt = 24, hideAfter = 520 } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const delta = y - lastY;
      setScrolled(y > scrolledAt);
      if (y < hideAfter) {
        setHidden(false);
        lastY = y;
      } else if (Math.abs(delta) > 6) {
        setHidden(delta > 0);
        lastY = y;
      }
    };
    const onScroll = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [scrolledAt, hideAfter]);

  return { scrolled, hidden };
}
